import Driver from 'driver.js';
import { ExtendedTranslatedNameType, TranslatedHistory, TranslatedNameType } from './translationTypes';

export interface SurveyV2State {
  survey: Survey | null;
  sections: SurveySection[];
  surveyHistoryDiff?: SurveyHistoryDiff;
  isFetchedHistory: boolean;
  highlightStepArray?: Driver.Step[];
}

export interface Survey {
  createdAt: string;
  updatedAt: string;
  version?: number;
  id: number;
  trialId: number;
  payload: SurveyPayload;
  evaluationType?: 'section';
  optionalSections?: boolean;
}

export interface SurveyPayload {
  version: number;
  sections: SurveySection[];
  openQuestions?: SurveyContextQuestion[];
}

export interface SurveySection {
  id: string; // uuid
  numCases: number;
  headline: string;
  questions: SurveyQuestion[];
  contextQuestions: SurveyContextQuestion[];
}

export interface SurveyQuestion {
  id: string; // uuid
  type: QuestionType;
  question: TranslatedNameType;
  required: boolean;
  excludeFromMarking: boolean;
  options?: TranslatedNameType[];
  placeholder?: string;
  freeTextPlaceholder?: string;
}

export type SurveyContextQuestion = Omit<SurveyQuestion, 'options'> & {
  options?: { answer: TranslatedNameType }[];
}

export interface SurveyHistory {
  id: number;
  createdAt: string;
  payload: SurveyPayload;
  surveyId: number;
  quipUserId: number;
  structureChanged: boolean;
}

export type SurveyHistoryDiff = {
  sections: {
    questions: {
      id: string;
      type: string;
      question?: TranslatedHistory;
      options?: Partial<TranslatedHistory>[];
    }[];
    contextQuestions: {
      id: string;
      type: string;
      question?: TranslatedHistory;
      options?: {
        answer: Partial<TranslatedHistory>;
      }[];
    }[];
  }[];
}

/**
 * This type is extended version of survey
 * it adds the history as a boolean type
 * in question and options for the survey highlight purpose
 */
export type ExtendedSurvey = Survey & {
  extended?: boolean;
  payload: SurveyPayload & {
    sections: SurveySection[] & {
      questions: SurveyQuestion[] & {
        question: ExtendedTranslatedNameType;
        options?: ExtendedTranslatedNameType[];
      }[];
      contextQuestions: SurveyContextQuestion[] & {
        question: ExtendedTranslatedNameType;
        options?: {
          answer: ExtendedTranslatedNameType;
        }[];
      }[];
    }[];
  };
}

export type QuestionType =
  'OneOf' |
  'MultipleChoice' |
  'ArbitraryString' |
  'IntegerPercentage' |
  ContextQuestionType;

type ContextQuestionType =
  'single' |
  'MultipleChoice' |
  'freeText';

/** Redux constants */

export const GET_SURVEY = 'GET_SURVEY';
export const UPDATE_SECTIONS = 'UPDATE_SECTIONS';
export const CLEAR_SURVEY_V2 = 'CLEAR_SURVEY_V2';
export const SET_SURVEY_HISTORY_DIFF = 'SET_SURVEY_HISTORY_DIFF';
export const IS_FETCHED_HISTORY = 'IS_FETCHED_HISTORY';
export const SET_HIGHLIGHT_STEP = 'SET_HIGHLIGHT_STEP';

/** Action Types */

type GetSurveyAction = {
  type: typeof GET_SURVEY;
  payload: Survey;
}

type UpdateSectionsSurveyAction = {
  type: typeof UPDATE_SECTIONS;
  payload: SurveySection[];
}

type ClearSurveyRecordAction = {
  type: typeof CLEAR_SURVEY_V2;
}

type SetSurveyHistoryDiffAction = {
  type: typeof SET_SURVEY_HISTORY_DIFF;
  payload: SurveyHistoryDiff;
}

type IsFetchedHistoryAction = {
  type: typeof IS_FETCHED_HISTORY;
  payload: boolean;
}

type SetHighlightStepAction = {
  type: typeof SET_HIGHLIGHT_STEP;
  payload: Driver.Step[] | undefined;
}

export type SurveyActionTypes =
  GetSurveyAction |
  UpdateSectionsSurveyAction |
  ClearSurveyRecordAction |
  SetSurveyHistoryDiffAction |
  IsFetchedHistoryAction |
  SetHighlightStepAction;
